import React from "react";
import { connect } from "react-redux";
import { Col } from "react-bootstrap";
import PropTypes from "prop-types";
import { Map } from "immutable";
import loadRecord from "MODULES/shared/decorators/loadRecord";
import {
  AuditFields,
  Authorized,
  CompanyName,
  LaunchModal,
  Notes,
  Attachments,
  Forbidden,
  Detail as TableDetail,
  Search
} from "MODULES/shared/components";
import resourceTypes from "MODULES/shared/types/resourceTypes";
import Contact from "MODULES/Orders/shared/Contact";
import { PaymentTerms } from "MODULES/companies/shared/components/PaymentTerms";
import EditBilling from "MODULES/RepairOrders/components/Edit/EditBilling";
import EditCompanyFields from "MODULES/RepairOrders/components/Edit/EditCompanyFields";
import { ACCOUNTING, WAREHOUSE } from "MODULES/users/constants";
import { dollars } from "LIB/formatters/currency";
import Subcomponents from "MODULES/RepairOrders/components/Detail/Teardowns/Subcomponents";
import { CODE, LINE_ITEMS } from "APP_ROOT/constants";
import { repairOrderLineItemsPagination } from "MODULES/RepairOrders/pagination";
import { SearchFilter } from "MODULES/shared/components/Pagination";
import { percent } from "LIB/formatters/number";
import ShippingDetails from "./ShippingDetails";
import {
  AMOUNT,
  ATTACHMENT_TYPES,
  EXPECTED_COMPONENT,
  INSURANCE_TYPES,
  NONE,
  PERCENT,
  SHOP_ASSIST,
  TEARDOWN
} from "../../constants";
import { find } from "../../actions";
import Header from "./Header";
import Create from "./LineItems/Create";
import HigherAssemblyTable from "./LineItems/HigherAssemblyTable";
import ShopAssistTable from "./LineItems/ShopAssistTable";
import DefaultRepairOrderTable from "./LineItems/DefaultRepairOrderTable";

class Detail extends React.Component {
  static propTypes = {
    repairOrder: PropTypes.instanceOf(Map)
  };

  state = {
    editPartModalOpen: false,
    editReturnToModalOpen: false,
    editShipToModalOpen: false,
    initialPartFormValues: {}
  };

  readOnlyItem = (lineItem = {}) =>
    (lineItem.action_requested === SHOP_ASSIST && lineItem.shipped) ||
    lineItem.scrap_report ||
    (lineItem.action_requested === EXPECTED_COMPONENT &&
      lineItem.quantity_received > 0);

  render() {
    const repairOrder = this.props.repairOrder;
    const cancelled = repairOrder.get("cancelled");
    return (
      <div>
        <Header repairOrder={repairOrder} />
        <div className="row">
          <div className="col-md-3">
            <div className="panel panel-default">
              <div className="panel-body">
                <h2 style={{ marginTop: 10 }}>
                  Details
                  <Forbidden roles={[WAREHOUSE]}>
                    {!cancelled && (
                      <EditCompanyFieldsModal record={repairOrder} />
                    )}
                  </Forbidden>
                </h2>
                <table className="table table-condensed">
                  <tbody>
                    <TableDetail
                      label="Rep Station Code"
                      value={repairOrder.getIn(["company", "code"])}
                    />
                    <TableDetail
                      label="Rep Station Name"
                      value={
                        <CompanyName
                          company={repairOrder.toJS().company}
                          context="repair"
                        />
                      }
                    />
                    <TableDetail
                      label="Rep Station Phone"
                      value={repairOrder.getIn(["company", "phone"])}
                    />
                  </tbody>
                </table>
                <Subcomponents repairOrder={repairOrder} />
                <hr className="hr-xs hr-dashed" />
                <Contact contact={repairOrder.get("contact")} />
                <hr className="hr-sm" />
                <h2>
                  Billing
                  <Authorized roles={[ACCOUNTING]}>
                    <Forbidden roles={[WAREHOUSE]}>
                      <EditBillingModal record={repairOrder} />
                    </Forbidden>
                  </Authorized>
                </h2>
                <table className="table table-condensed">
                  <tbody>
                    <TableDetail
                      label="Credit Terms"
                      value={
                        <PaymentTerms
                          paymentTerms={repairOrder.get("payment_terms")}
                        />
                      }
                    />
                  </tbody>
                </table>
                <hr className="hr-xs hr-dashed" />
                <ShippingDetails shippingType="ship_to" record={repairOrder}>
                  <table className="table table-condensed">
                    <tbody>
                      <TableDetail
                        label="Insurance"
                        value={insuranceValue(repairOrder)}
                      />
                    </tbody>
                  </table>
                </ShippingDetails>
                <hr className="hr-xs hr-dashed" />
                <ShippingDetails
                  shippingType="return_to"
                  record={repairOrder}
                />
                <hr className="hr-xs hr-dashed" />
                <AuditFields record={repairOrder} multiLine />
              </div>
            </div>
          </div>
          <Col md={9}>
            <h2 style={{ marginTop: 26 }}>
              Repair Order Details
              <Forbidden roles={[WAREHOUSE]}>
                <AddPartModal repairOrder={repairOrder} />
              </Forbidden>
            </h2>
            {repairOrder.get(LINE_ITEMS).size > 15 && (
              <Search search={repairOrderLineItemsPagination} syncWithUrl>
                {searchProps => (
                  <SearchFilter
                    {...searchProps}
                    placeholder="Search by typing a part number"
                  />
                )}
              </Search>
            )}
            <br />
            <DefaultRepairOrderTable repairOrder={repairOrder} />
            <HigherAssemblyTable repairOrder={repairOrder} />
            <ShopAssistTable repairOrder={repairOrder} />
            <Notes
              resource={repairOrder}
              resourceType={resourceTypes.repairOrder}
              resourceKey="repairOrder"
            />
            <hr className="hr-dashed" />
            <Attachments
              resource={repairOrder}
              resourceType={resourceTypes.repairOrder}
              resourceKey="repairOrder"
              attachmentTypes={ATTACHMENT_TYPES}
              multiple
              referenceOptions={[
                {
                  text: repairOrder.get(CODE),
                  value: repairOrder.get(CODE)
                },
                ...repairOrder
                  .get(LINE_ITEMS)
                  .toJS()
                  .map(l => ({
                    text: `SN-${l.serial_number}`,
                    value: `SN-${l.serial_number}`
                  }))
              ]}
            />
          </Col>
        </div>
      </div>
    );
  }
}

export default connect(state => ({
  repairOrder: state.repairOrderDetail.get("repairOrder")
}))(
  loadRecord({
    reducer: "repairOrderDetail",
    name: "repairOrder",
    action: find
  })(Detail)
);

function EditBillingModal({ record }) {
  return (
    <LaunchModal
      modal={EditBilling}
      buttonClass="btn btn-primary btn-hollow pull-right"
      buttonIcon="fa-pencil"
      text="Edit"
      record={record}
    />
  );
}

function insuranceValue(repairOrder) {
  const insuranceType = repairOrder.get("insurance_type");
  if (insuranceType === NONE) {
    return INSURANCE_TYPES[NONE].value;
  } else if (insuranceType === AMOUNT) {
    return dollars(repairOrder.get(INSURANCE_TYPES[AMOUNT].key));
  } else if (insuranceType === PERCENT) {
    return percent(Number(repairOrder.get(INSURANCE_TYPES[PERCENT].key)), 100);
  }

  return null;
}

EditBillingModal.propTypes = {
  record: PropTypes.shape({}).isRequired
};

function EditCompanyFieldsModal({ record }) {
  return (
    <LaunchModal
      modal={EditCompanyFields}
      buttonClass="btn btn-primary btn-hollow pull-right"
      buttonIcon="fa-pencil"
      text="Edit"
      record={record}
    />
  );
}

EditCompanyFieldsModal.propTypes = {
  record: PropTypes.shape({}).isRequired
};

function AddPartModal({ repairOrder }) {
  const tearDown = repairOrder
    .get("line_items")
    .some(l => l.get("action_requested") === TEARDOWN);
  const shippedTeardown = repairOrder
    .get("line_items")
    .some(l => l.get("action_requested") === TEARDOWN && l.get("shipped"));

  if (!repairOrder.get("cancelled") && tearDown && !shippedTeardown) {
    return null;
  }

  return (
    <LaunchModal
      buttonClass="btn btn-primary btn-hollow pull-right"
      modal={Create}
      repairOrder={repairOrder}
      text="Add Part"
    />
  );
}

AddPartModal.propTypes = {
  repairOrder: PropTypes.shape({}).isRequired
};



// WEBPACK FOOTER //
// ./app/modules/RepairOrders/components/Detail/Detail.jsx